import { EventClickArg, EventContentArg, EventInput, EventSourceFuncArg } from '@fullcalendar/core';
import { FC, useCallback, useRef, useState } from "react";
import { FieldFilterType, IEntity } from '../../../base/types';
import { ICalendarDataQuery, useLazyReadCalendarDataQuery } from "./calendarDataApi";
import { ICalendarOptions, IEntityFilter } from "../../../base/models";
import { useNavigate, useParams } from 'react-router-dom';

import { EventDataProvider } from './EventDataProvider';
import FullCalendar from "@fullcalendar/react";
import { Spinner } from '../../../widgets';
import clsx from 'clsx';
import dayGridPlugin from '@fullcalendar/daygrid';
import moment from 'moment';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useAuth } from "../../../../modules/auth";

interface ICalendarProps {
    calendarOptions: ICalendarOptions,
    className?: string,
    eventClassNamesExpr?: string,
    children?: React.ReactNode
}

export const Calendar: FC<ICalendarProps> = ({
    calendarOptions, eventClassNamesExpr, className, children
}) => {
    const { user } = useAuth()
    const { siteId } = useParams()
    const navigate = useNavigate()

    const [readCalendarData] = useLazyReadCalendarDataQuery()
    const calendarRef = useRef<FullCalendar>(null)
    const [isLoading, setLoading] = useState<boolean>(false)

    const eventContent = (arg: EventContentArg) => {
        const { event, view } = arg;
        const classNames = view.type === "dayGridMonth" ? '' :
            getEventClassNames(event.extendedProps)

        return <EventDataProvider data={event.extendedProps}>
            <div className={clsx('overflow-hidden', classNames)} title={event.title}>
                {children}
            </div>
        </EventDataProvider>
    }

    const getEventClassNames = (props: IEntity) => {
        try {
            // eslint-disable-next-line
            const classNameExpression = new Function('moment', `${eventClassNamesExpr}`)
                .bind(props);
            const classNamesObj = classNameExpression(moment);
            return clsx(classNamesObj)
        } catch (error) {
            console.log("error", error)
        }

        return ""
    }

    const parseExpand = (expand?: string): [entity?: string, options?: string] => {
        const pattern = /^(\w+)(?:\((.*)\))?$/;
        const matches = expand?.match(pattern);

        // Extract entity name and filter option (if available)
        if (!matches)
            return [];

        const entity = matches[1] || undefined;
        const options = matches[2] || undefined;

        return [entity, options];
    }

    const fetchEventData = useCallback((
        info: EventSourceFuncArg,
        success: (eventInputs: EventInput[]) => void,
        failure: (error: Error) => void
    ) => {
        const dataSource = calendarOptions;
        if (!dataSource || !dataSource.entity || !dataSource.start || !dataSource.end) {
            failure(new Error('Invalid parameters.'))
            return;
        }

        const expand = (dataSource.expand && user?.contactId) ? dataSource.expand.replace('{user.ContactId}', user.memberOf[0].entityId || '') : '';
        const filters: IEntityFilter[] = dataSource.filters || []
        const query: ICalendarDataQuery = {
            ...dataSource,
            select: [],
            expand,
            filters: [{ group: 0, filters }, {
                group: 1,
                filters: [
                    {field: dataSource.start,type: FieldFilterType.Filter,value: `>='${moment(info.startStr).startOf("day").format("YYYY-MM-DDTHH:mm:ss")}'`},
                    {field: dataSource.end,type: FieldFilterType.Filter,value: `<='${moment(info.endStr).startOf("day").subtract(1, "second").format("YYYY-MM-DDTHH:mm:ss")}'`}
                ]
            }],
            expandFilters: (dataSource.expandFilters && user?.contactId) ? dataSource.expandFilters.replace('{user.ContactId}', user.memberOf[0].entityId || '') : ""
        }

        setLoading(true)
        readCalendarData(query)
            .unwrap()
            .then(data => {
                const [entity] = parseExpand(expand);
                const records = !entity ?
                    data.records :
                    data.records.filter(p => p[entity] && p[entity].length > 0);

                const events = records.map<EventInput>(p => ({
                    id: dataSource.systemId ? p[dataSource.systemId] : undefined,
                    start: dataSource.start ? p[dataSource.start] : undefined,
                    end: dataSource.end ? p[dataSource.end] : undefined,
                    title: dataSource.title ? p[dataSource.title] : undefined,
                    classNames: clsx("shadow-sm px-2 py-1 overflow-hidden", getEventClassNames(p)),
                    ...p
                }))

                success(events)
            })
            .catch(err => failure(err))
            .finally(() => setLoading(false))
        // eslint-disable-next-line
    }, [calendarOptions])

    const formatWeekHeaderDate = (date: Date) => {
        return moment(date).format('ddd D MMM');
    };

    const dayHeaderContent = (args: any) => {
        const { date, view } = args;
        // Check if the current view is week view
        if (view.type === 'timeGridWeek') {
            return (
                <div>
                    {formatWeekHeaderDate(date)}
                </div>
            );
        } else if (view.type === 'day') { // Check if the current view is day view
            return (
                <div>
                    {formatWeekHeaderDate(date)}
                </div>
            );
        }
        // For other views, return default header content
        return (
            <div>
                {moment(date).format('ddd')}
            </div>
        );
    };

    const onEventClick = (e: EventClickArg) => {
        const { event } = e;
        if (!calendarOptions.eventPageId || !siteId || !event.id)
            return;

        navigate(`/site(${siteId})/page(${calendarOptions.eventPageId})?id=${event.id}`)
    }

    return <div className={clsx(className)}>
        <Spinner show={isLoading} />
        <FullCalendar
            height={"90vh"}
            lazyFetching={true}
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin]}
            headerToolbar={{ center: 'dayGridMonth,timeGridWeek,day' }}
            initialView="timeGridWeek"
            weekends={true}
            allDaySlot={false}
            themeSystem='default'
            eventClick={onEventClick}
            eventContent={eventContent}
            firstDay={1}
            dayMaxEvents={8}
            eventMaxStack={8}
            locale='en'
            views={{
                dayGridMonth: { buttonText: 'Month' },
                timeGridWeek: { buttonText: 'Week' },
                day: { type: 'timeGridDay', duration: { days: 1 } },
            }}
            events={fetchEventData || []}
            dayHeaderContent={dayHeaderContent}
        />
    </div>
}
